import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

import {
    Box,
    CircularProgress,
    Dialog,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { useSelector, useDispatch } from 'react-redux';

import dayjs from 'dayjs';

import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import { customStyle } from 'shared/config/Styles';
import DialogHeader from 'shared/ui/DialogHeader';

import { copyText, copyTable } from './EmailClipboard';
import {
    oceanImportEmailConfig,
    oceanExportEmailConfig,
    airImportEmailConfig,
    airExportEmailConfig,
    truckingEmailConfig,
} from 'data/emailConfig';
import { buildEmailData } from './emailDataBuilder';
import { fieldMapping } from '../../lib/shipmentFieldMapping';
import { buildShipmentList } from '../../lib/buildShipmentList';

import { setSnackbar } from 'shared/store/slices/snackBarSlice';

// ── 헬퍼 ─────────────────────────────────────────────────────────────
const getEmailConfig = (tabValue) => {
    if (tabValue === 0) return oceanImportEmailConfig;
    if (tabValue === 1) return oceanExportEmailConfig;
    if (tabValue === 2) return airImportEmailConfig;
    if (tabValue === 3) return airExportEmailConfig;
    return truckingEmailConfig;
};

export const resolveCellValue = (keyName, source, rowInfo, tabValue) => {
    if (!keyName) return '-';
    const val = source && keyName in source ? source[keyName] : rowInfo?.[keyName];
    if (val == null) return '-';
    if (Array.isArray(val)) {
        return [...new Set(val.map((v) => v?.name || v?.code || v).filter(Boolean))].join('/ ') || '-';
    }
    if (typeof val === 'object') {
        const resolved = tabValue === 2 || tabValue === 3 ? val?.code || val?.name : val?.name;
        return resolved || String(Object.values(val)[0] ?? '') || '-';
    }
    return String(val) || '-';
};

// ── 서브 컴포넌트 ─────────────────────────────────────────────────────
const CustomCopyHeader = ({ label, onClick, loading, boxStyle, copyStyle }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', ...boxStyle }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '13px' }}>{label}</Typography>
        <Tooltip title="Copy">
            <ContentCopyIcon
                sx={{
                    fontSize: 18,
                    color: 'white',
                    bgcolor: 'primary.dark',
                    borderRadius: '15px',
                    p: '1.5px',
                    cursor: 'pointer',
                    ...copyStyle,
                }}
                onClick={onClick}
            >
                {loading ? <CircularProgress size={24} /> : ''}
            </ContentCopyIcon>
        </Tooltip>
    </Box>
);

const StatusMessageCell = ({ messages = [], splitList = [], item }) => {
    const lastIdx = messages.length - 1;

    // alias_id 기준으로 그룹핑
    // alias_id === 0 : house 또는 container 메시지 (그룹 키 없음)
    // alias_id !== 0 : split 메시지 → splitList에서 alias_name 조회
    const grouped = messages.reduce((acc, msg) => {
        const key = msg.alias_id !== 0 ? `alias_${msg.alias_id}` : 'default';
        if (!acc[key]) acc[key] = { aliasId: msg.alias_id, msgs: [] };
        acc[key].msgs.push(msg);
        return acc;
    }, {});

    // 그룹 순서: default 먼저, 이후 alias 순
    const sortedGroups = Object.values(grouped).sort((a, b) => {
        if (a.aliasId === 0) return -1;
        if (b.aliasId === 0) return 1;
        return a.aliasId - b.aliasId;
    });

    return (
        <TableCell sx={{ ...customStyle[item.style] }} colSpan={item.colSpan} component={item.component}>
            {sortedGroups.map((group, groupIdx) => {
                // split 그룹이면 splitList에서 alias_name 조회
                const isSplitGroup = group.aliasId !== 0;
                const aliasName = isSplitGroup
                    ? (splitList.find((sp) => sp.id === group.aliasId)?.alias_name ?? `Split ${group.aliasId}`)
                    : null;

                const groupLastIdx = group.msgs.length - 1;

                return (
                    <Box key={groupIdx}>
                        {/* Split 헤더 라벨 */}
                        {isSplitGroup && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'block',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    color: 'inherit',
                                }}
                            >
                                {aliasName}
                            </Box>
                        )}

                        {/* 메시지 목록 */}
                        {group.msgs.map((msg, msgIdx) => {
                            const isBold = msgIdx === groupLastIdx;
                            const isHouse = msg.child_id === 0;
                            const formatted = msg.event_date ? dayjs(msg.event_date).format('MM/DD') : '';
                            return (
                                <Box
                                    key={msg.id}
                                    component="span"
                                    sx={{
                                        display: 'block',
                                        fontSize: '10px',
                                        fontWeight: isBold ? 'bold' : 'normal',
                                        color: isHouse ? 'red' : 'inherit',
                                    }}
                                >
                                    {isHouse ? msg.message : `${formatted} ${msg.message}`}
                                </Box>
                            );
                        })}
                    </Box>
                );
            })}
        </TableCell>
    );
};

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────
export default function EmailBuilderDialog(props) {
    const { emailTemplateOpen, setEmailTemplateOpen, selectedRow } = props;
    const dispatch = useDispatch();

    const tabValue = useSelector((state) => state.tabValueForm);
    const hblShipmentList = useSelector((state) => state[fieldMapping[tabValue]?.hbl]);
    const cntrShipmentList = useSelector((state) => state[fieldMapping[tabValue]?.cntr]);
    const splitShipmentList = useSelector((state) => state[fieldMapping[tabValue]?.split]);
    const statusMessageList = useSelector((state) => state[fieldMapping[tabValue]?.statusMessage]);
    const [shipmentList, setShipmentList] = useState(null);
    const [rowInfo, setRowInfo] = useState({});
    const [containers, setContainers] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    const shippingAdviceRef = useRef(null);
    const preAlertRef = useRef(null);
    const statusUpdateRef = useRef(null);
    const subjectRefs = {
        shippingAdviceSubject: useRef(null),
        preAlertSubject: useRef(null),
        customsSubject: useRef(null),
        deliveryOrderSubject: useRef(null),
    };

    const handleDialogClose = () => {
        setEmailTemplateOpen(false);
        setLoaded(false);
    };

    // ── Step 1: nested shipmentList 빌드 ─────────────────────────────
    useEffect(() => {
        buildShipmentList(
            tabValue,
            selectedRow?.[0],
            hblShipmentList,
            cntrShipmentList,
            splitShipmentList,
            setShipmentList,
        );
    }, [selectedRow, hblShipmentList, cntrShipmentList, splitShipmentList, tabValue]);

    // ── Step 2: shipmentList → rowInfo + containers ──────────────────
    useEffect(() => {
        if (!shipmentList?.id) return;
        const { rowInfo: built, containers: builtContainers } = buildEmailData(
            shipmentList,
            statusMessageList,
            tabValue,
        );

        setRowInfo(built);
        setContainers(builtContainers);
        setLoaded(true);
    }, [shipmentList, statusMessageList, tabValue]);

    // ── emailConfig ──────────────────────────────────────────────────
    const emailConfig = getEmailConfig(tabValue);

    // ── Subject 생성 ─────────────────────────────────────────────────
    const buildSubject = (subject) => {
        // 배열이면 그대로, 객체면 배열로 감싸기, null/undefined면 빈 배열
        const consigneeList = Array.isArray(rowInfo?.il_trade_partner_id_consignee)
            ? rowInfo.il_trade_partner_id_consignee
            : rowInfo?.il_trade_partner_id_consignee
              ? [rowInfo.il_trade_partner_id_consignee]
              : [];

        const consignees = [...new Set(consigneeList.map((c) => c?.name).filter(Boolean))].join('/ ');

        const refs_ = (rowInfo?.reference ?? []).join('/ ');
        const vol = rowInfo?.volume || rowInfo?.quantity || 'VOLUME (NO INFO)';
        const volPlain = rowInfo?.volume_plain || vol;
        const isOcean = tabValue === 0 || tabValue === 1;
        const isAir = tabValue === 2 || tabValue === 3;
        const mblLabel = isAir ? 'MAWB#' : 'MBL#';
        const hblLabel = isAir ? 'HAWB#' : 'HBL#';
        const mblVal = isOcean ? rowInfo?.mbl : rowInfo?.mawb;
        const hblVal = isOcean
            ? (rowInfo?.hbl ?? []).filter(Boolean).join('/ ')
            : (rowInfo?.hawb ?? []).filter(Boolean).join('/ ');
        const cntrPart = isOcean ? `CNTR# ${(rowInfo?.cntr_number ?? []).filter(Boolean).join('/ ')}` : '';
        const deliverTo = [
            ...new Set((rowInfo?.il_location_id_deliver_to ?? []).filter(Boolean).map((item) => item?.name || item)),
        ].join('/ ');

        switch (subject) {
            case 'shippingAdviceSubject':
                return `[INSIGHT] SHIPPING ADVICE/ ${consignees}/ ${refs_} ${vol}`;
            case 'preAlertSubject':
                return `[INSIGHT] ${consignees} ${refs_} ${mblLabel} ${mblVal || ''} ${hblLabel} ${hblVal} ${cntrPart} ${vol}`.trim();
            case 'customsSubject':
                return `[INSIGHT] CUSTOMS CLEARANCE/ ${mblLabel} ${mblVal || ''} ${hblLabel} ${hblVal} ${cntrPart} ${volPlain}`.trim();
            case 'deliveryOrderSubject':
                if (isOcean) return `[INSIGHT] DELIVERY ORDER TO ${deliverTo} ${cntrPart} ${vol}`;
                if (isAir) return `[INSIGHT] DELIVERY ORDER TO ${deliverTo} HAWB# ${hblVal} ${vol}`;
                return `[INSIGHT] DELIVERY ORDER TO ${deliverTo} BOL# ${rowInfo?.bol || ''} ${vol}`;
            default:
                return '';
        }
    };

    // ── 표시 항목 필터 ────────────────────────────────────────────────
    const visibleSections = ['shippingAdvice', 'preAlert'].filter((s) => !(s === 'preAlert' && tabValue === 4));

    const visibleSubjects = [
        'shippingAdviceSubject',
        'preAlertSubject',
        'customsSubject',
        'deliveryOrderSubject',
    ].filter((s) => {
        if (s === 'preAlertSubject' && tabValue === 4) return false;
        if (s === 'customsSubject' && (tabValue === 1 || tabValue === 3 || tabValue === 4)) return false;
        return true;
    });

    // ── 셀 렌더링 ─────────────────────────────────────────────────────
    const renderCell = (item, itemIdx, source = {}) => {
        if (item.title) {
            return (
                <TableCell
                    key={itemIdx}
                    sx={{ ...customStyle[item.style] }}
                    colSpan={item.colSpan}
                    component={item.component}
                >
                    {item.title}
                </TableCell>
            );
        }
        if (item.style === 'tableCellStyle_statusMessage') {
            return (
                <StatusMessageCell
                    key={itemIdx}
                    messages={source?.statusMessages ?? []}
                    splitList={source?.splitList ?? []} // ← 추가
                    item={item}
                />
            );
        }

        return (
            <TableCell
                key={itemIdx}
                sx={{ ...customStyle[item.style] }}
                colSpan={item.colSpan}
                component={item.component}
            >
                {resolveCellValue(item.description, source, rowInfo, tabValue)}
            </TableCell>
        );
    };

    // ── Status Update 렌더링 ──────────────────────────────────────────
    const renderStatusUpdate = () =>
        emailConfig.statusUpdate.map((list, idx) => {
            const isHeaderRow = list.every((item) => !!item.title);

            if (isHeaderRow) {
                return (
                    <TableRow key={`header-${idx}`} sx={{ '& .MuiTableCell-root': { p: '3px' } }}>
                        {list.map((item, itemIdx) => renderCell(item, itemIdx))}
                    </TableRow>
                );
            }

            if (containers.length > 0) {
                return containers.map((cntr, cntrIdx) => (
                    <TableRow key={`cntr-${idx}-${cntrIdx}`} sx={{ '& .MuiTableCell-root': { p: '3px' } }}>
                        {list.map((item, itemIdx) => renderCell(item, itemIdx, cntr))}
                    </TableRow>
                ));
            }

            return (
                <TableRow key={`row-${idx}`} sx={{ '& .MuiTableCell-root': { p: '3px' } }}>
                    {list.map((item, itemIdx) => renderCell(item, itemIdx, {}))}
                </TableRow>
            );
        });

    // ── render ────────────────────────────────────────────────────────
    return (
        <Paper>
            <Dialog
                onClose={handleDialogClose}
                open={emailTemplateOpen}
                maxWidth="lg"
                fullWidth
                PaperComponent={PaperComponent}
                PaperProps={{ sx: { borderRadius: '18px' } }}
            >
                {loaded ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'auto' }}>
                        <DialogHeader
                            cursor
                            title={`EMAIL TEMPLATE | ${selectedRow?.[0]?.il_opus_reference_id ?? ''}`}
                            onClose={handleDialogClose}
                        />

                        <Box sx={{ p: 0.5 }}>
                            {/* ── Shipping Advice / Pre Alert ── */}
                            <Grid container spacing={2} sx={{ p: 1, width: '80%' }}>
                                {visibleSections.map((section, index) => {
                                    const sectionRef = section === 'shippingAdvice' ? shippingAdviceRef : preAlertRef;
                                    return (
                                        <Grid key={index} size={6}>
                                            <CustomCopyHeader
                                                label={section === 'shippingAdvice' ? 'SHIPPING ADVICE' : 'PRE ALERT'}
                                                onClick={() => copyTable(sectionRef, setLoading, setSnackbar, dispatch)}
                                                loading={loading}
                                            />
                                            <TableContainer ref={sectionRef}>
                                                <Table
                                                    sx={{
                                                        mt: '5px',
                                                        border: '1px solid black',
                                                        width: '100%',
                                                        borderCollapse: 'collapse',
                                                    }}
                                                >
                                                    <TableBody>
                                                        {emailConfig[section].map((list, idx) => (
                                                            <TableRow
                                                                key={idx}
                                                                sx={{ '& .MuiTableCell-root': { p: '3px' } }}
                                                            >
                                                                {list.map((item, itemIdx) =>
                                                                    renderCell(item, itemIdx, rowInfo),
                                                                )}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            {/* ── Status Update ── */}
                            <Box sx={{ p: 1, pt: 0 }}>
                                <CustomCopyHeader
                                    label="STATUS UPDATE"
                                    onClick={() => copyTable(statusUpdateRef, setLoading, setSnackbar, dispatch)}
                                    loading={loading}
                                />
                                <TableContainer ref={statusUpdateRef}>
                                    <Table
                                        sx={{
                                            mt: '5px',
                                            border: '1px solid black',
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                        }}
                                    >
                                        <TableBody>{renderStatusUpdate()}</TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            {/* ── Subject Lines ── */}
                            {visibleSubjects.map((subject) => (
                                <Box key={subject} sx={{ px: 2 }}>
                                    <CustomCopyHeader
                                        label={subject.replace(/([A-Z])/g, ' $1').toUpperCase()}
                                        onClick={() => copyText(subjectRefs[subject], setSnackbar, dispatch)}
                                        boxStyle={{ mt: '7px' }}
                                    />
                                    <Typography ref={subjectRefs[subject]} sx={{ fontSize: '11px' }}>
                                        {buildSubject(subject)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <CustomSkeleton2 />
                )}
            </Dialog>
        </Paper>
    );
}
